import { FC } from "react";
import { useTranslation } from "react-i18next";

import { RosterRow, RosterStatus } from "../../../hooks/useCollaboratorRoster";

/**
 * Status badge for a roster row.
 *
 * Deliberately distinguishes "expired" from "pending": an invitation dies
 * after 72h, and a manager who cannot see that difference has no way to know
 * whether to keep waiting or to resend.
 */
const STYLES: Record<
  RosterStatus,
  { bg: string; fg: string; border: string }
> = {
  member: { bg: "#e8f5e9", fg: "#1b5e20", border: "#a5d6a7" },
  pending: { bg: "#fff8e1", fg: "#7a4f01", border: "#ffe082" },
  expired: { bg: "#ffebee", fg: "#b71c1c", border: "#ef9a9a" },
  revoked: { bg: "#eceff1", fg: "#546e7a", border: "#cfd8dc" },
};

/** Hours remaining before a pending invitation expires, or null. */
const hoursLeft = (expiresAt: string | null): number | null => {
  if (!expiresAt) return null;
  const ms = new Date(expiresAt).getTime() - Date.now();
  return ms > 0 ? Math.ceil(ms / 3_600_000) : null;
};

export const CollaboratorStatusChip: FC<{ row: RosterRow }> = ({ row }) => {
  const { t } = useTranslation();
  const style = STYLES[row.status] ?? STYLES.revoked;

  const label = (() => {
    switch (row.status) {
      case "member":
        return t("collaborator.statusAccepted", "Invitation acceptée");
      case "pending":
        return t("collaborator.statusPending", "En attente d'acceptation");
      case "expired":
        return t("collaborator.statusExpired", "Invitation expirée");
      default:
        return t("collaborator.statusRevoked", "Invitation révoquée");
    }
  })();

  const remaining = row.status === "pending" ? hoursLeft(row.expiresAt) : null;

  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <span
        style={{
          backgroundColor: style.bg,
          color: style.fg,
          border: `1px solid ${style.border}`,
          padding: "2px 8px",
          borderRadius: 12,
          fontSize: 11,
          fontWeight: 700,
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </span>
      {remaining !== null && (
        <span style={{ fontSize: 11, color: "#8d8d8d", whiteSpace: "nowrap" }}>
          {t("collaborator.expiresInHours", "expire dans {{count}} h", {
            count: remaining,
          })}
        </span>
      )}
    </span>
  );
};
